<template>
  <div class="weapon-hud">
    <div class="weapon-info">
      <div class="weapon-name">{{ currentWeaponName }}</div>
      <div class="ammo-counter" v-if="!isInfiniteAmmo">
        {{ currentAmmo }} <span class="separator">/</span> {{ totalAmmo }}
      </div>
      <div class="ammo-counter infinite" v-else>
        ∞
      </div>
    </div>
    
    <div class="weapon-selector">
      <div 
        v-for="(weapon, index) in weapons" 
        :key="weapon.type"
        :class="[
          'weapon-slot', 
          { active: currentWeapon === weapon.type },
          { faded: isSwitching && currentWeapon !== weapon.type }
        ]"
        @click="selectWeapon(weapon.type)"
      >
        <div class="weapon-key">{{ index + 1 }}</div>
        <div class="weapon-label">{{ weapon.name }}</div>
      </div>
    </div>
    
    <div class="status-overlay" v-if="isReloading || isSwitching">
      <div class="status-message" v-if="isReloading">Reloading...</div>
      <div class="status-message" v-if="isSwitching">Switching...</div>
    </div>
  </div>
</template>

<script>
import { WEAPON_TYPES, WEAPONS } from '../weapons';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'WeaponHUD',
  props: {
    currentWeapon: {
      type: String,
      default: WEAPON_TYPES.PISTOL
    },
    currentAmmo: {
      type: Number,
      default: 0
    },
    totalAmmo: {
      type: Number,
      default: 0
    },
    isReloading: {
      type: Boolean,
      default: false
    },
    isSwitching: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    weapons() {
      // Return an array of weapon info objects
      return [
        {
          type: WEAPON_TYPES.KNIFE,
          name: WEAPONS[WEAPON_TYPES.KNIFE].name
        },
        {
          type: WEAPON_TYPES.PISTOL,
          name: WEAPONS[WEAPON_TYPES.PISTOL].name
        },
        {
          type: WEAPON_TYPES.MACHINE_GUN,
          name: WEAPONS[WEAPON_TYPES.MACHINE_GUN].name
        }
      ];
    },
    currentWeaponName() {
      return WEAPONS[this.currentWeapon]?.name || 'Unknown';
    },
    isInfiniteAmmo() {
      return this.totalAmmo === Infinity || this.currentWeapon === WEAPON_TYPES.KNIFE;
    }
  },
  methods: {
    selectWeapon(weaponType) {
      // Emit event to parent component to switch weapons
      this.$emit('weapon-selected', weaponType);
    }
  }
})
</script>

<style scoped>
.weapon-hud {
  position: absolute;
  bottom: 20px;
  right: 20px;
  color: white;
  font-family: 'Arial', sans-serif;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 1000;
}

.weapon-info {
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 10px 15px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  width: 200px;
}

.weapon-name {
  font-size: 18px;
  font-weight: bold;
}

.ammo-counter {
  font-size: 18px;
  font-weight: bold;
}

.ammo-counter.infinite {
  font-size: 24px;
}

.separator {
  opacity: 0.6;
  margin: 0 3px;
}

.weapon-selector {
  display: flex;
  gap: 10px;
}

.weapon-slot {
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 60px;
}

.weapon-slot:hover {
  background: rgba(0, 0, 0, 0.8);
}

.weapon-slot.active {
  border-color: #ffcc00;
  background: rgba(30, 30, 30, 0.8);
}

.weapon-slot.faded {
  opacity: 0.5;
}

.weapon-key {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-bottom: 5px;
}

.weapon-label {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.status-overlay {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: bold;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}
</style> 